import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Sports } from '@gin-betting/constants';
import { App, View } from '@gin-betting/core';
import cn from 'classnames';

import { MetaSide, SportEventMetaKey } from 'app-constants';
import { PenaltyCard } from 'components';
import globalMsgs from 'messages';
import { ExtendedScoreInfo, MatchPartInfo } from 'types';
import {
    getMapNameFromMeta,
    getScoreTitle,
    isMetaTypeEqualScoreType,
} from 'utils';

import './scoreItem.scss';

interface Props {
    meta: View.Meta[];
    score: ExtendedScoreInfo;
    currentMatchPart: MatchPartInfo | null;
    sportId: string | undefined;
}

const ACTIVE_SCORE_TYPES = [App.ScoreTypes.TYPE_GAME_POINTS];

const ScoreItem: React.FC<Props> = ({
    score,
    currentMatchPart,
    meta,
    sportId,
}) => {
    const { type, home, away, subScore } = score;
    const { formatMessage } = useIntl();

    const scoreName = useMemo(() => {
        if (type === App.ScoreTypes.TYPE_TOTAL) {
            return formatMessage(globalMsgs.score);
        }

        if (type === App.ScoreTypes.TYPE_RED_CARD) {
            return <PenaltyCard typeCard="red" />;
        }

        const title = getScoreTitle({
            meta,
            score,
            currentMatchPart,
            isOverview: true,
        });

        if (
            sportId === Sports.ESPORTS_COUNTER_STRIKE &&
            score.type === App.ScoreTypes.TYPE_MAP
        ) {
            return getMapNameFromMeta({
                format: 'short',
                map: title,
            });
        }

        return title;
    }, [type, score, currentMatchPart, meta, sportId, formatMessage]);

    const isTotal = type === App.ScoreTypes.TYPE_TOTAL;

    const isActive =
        ACTIVE_SCORE_TYPES.includes(score.type) ||
        (currentMatchPart &&
            currentMatchPart.indexNumber === score.indexNumber &&
            isMetaTypeEqualScoreType(currentMatchPart.type, score.type));

    let sideAway: MetaSide | undefined;

    let sideHome: MetaSide | undefined;

    if (isActive && type === App.ScoreTypes.TYPE_MAP) {
        sideAway = meta.find(({ name }) => name === SportEventMetaKey.SideAway)
            ?.value as MetaSide | undefined;
        sideHome = meta.find(({ name }) => name === SportEventMetaKey.SideHome)
            ?.value as MetaSide | undefined;
    }

    const items: {
        value: string;
        side: MetaSide | undefined;
        subValue: string | undefined;
    }[] = [
        { value: home, side: sideHome, subValue: subScore?.home },
        { value: away, side: sideAway, subValue: subScore?.away },
    ];

    return (
        <div
            styleName={cn('container', {
                'is-total': isTotal,
                'is-active': isActive,
            })}
        >
            <div styleName="title" className="__app-ScoreItem-title">
                {scoreName}
            </div>

            <div styleName="scores" className="__app-ScoreItem-scores">
                {items.map(({ value, side, subValue }, idx) => {
                    const withSubValue =
                        isActive && typeof subValue === 'string';

                    return (
                        // eslint-disable-next-line react/no-array-index-key
                        <div styleName="score" key={idx}>
                            <div
                                styleName={cn('backlight', {
                                    'is-terrorist': side === MetaSide.Terrorist,
                                    'is-contr-terrorist':
                                        side === MetaSide.ContrTerrorist,
                                    'is-radiant': side === MetaSide.Radiant,
                                    'is-dire': side === MetaSide.Dire,
                                    'with-sub-value': withSubValue,
                                })}
                            >
                                <div styleName="value">{value}</div>
                                {withSubValue && (
                                    <div styleName="sub-value">
                                        ({subValue})
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ScoreItem;
